import { motion } from "framer-motion";
import React, {
	memo,
	useMemo,
	forwardRef,
	useState,
	useEffect,
	useCallback,
} from "react";
import {
	SurfaceActionBar,
	SurfaceImageViewer,
	SurfaceNavBar,
} from "./SurfacesCoreUI";

import "./surface.css";
import anim from "../../utils/anim";
import useTrack from "../../hooks/useTrack";
import useDimensions from "../../hooks/useDimensions";
import useData from "../../hooks/useData";

const Surface = memo(
	forwardRef(
		(
			{
				children,
				className = "",
				handleClose,
				imageView,
				actionBar,
				navBar,
				displayEvent,
				skipEvent,
				showFullCard = false,
				showSurfaceTab = true,
				surfaceTabContent,
				offsetTopScale,
				...props
			},
			// eslint-disable-next-line no-unused-vars
			ref
		) => {
			const [trackedDisplay, setTrackedDisplay] = useState(false);
			// const { innerHeight } = useContext(SurfaceDimensionsContext);
			const { innerHeight, inDesktopOnly } = useDimensions();
			const track = useTrack();

			const { scrapedInformation: { email = "" } = {} } = useData();

			const offsetTopScaleComputed = useMemo(
				() =>
					offsetTopScale
						? offsetTopScale
						: inDesktopOnly
						? showSurfaceTab
							? 1.65
							: 1.75
						: showSurfaceTab
						? 1.9
						: 2.1,
				[offsetTopScale, showSurfaceTab, inDesktopOnly]
			);

			const handleSurfaceClose = useCallback(() => {
				if (skipEvent) {
					track(skipEvent.name, skipEvent.properties);
				}
				handleClose();
			}, [track, skipEvent, handleClose]);

			useEffect(() => {
				if (trackedDisplay || !displayEvent) {
					return;
				}
				track(displayEvent.name, displayEvent.properties);
				setTrackedDisplay(true);
			}, [trackedDisplay, displayEvent, track]);

			return (
				<section
					className={`surface-container ${className}`}
					role="document"
				>
					{!navBar?.hidden && (
						<SurfaceNavBar
							handleClose={handleSurfaceClose}
							{...(navBar?.props || {})}
						>
							{navBar?.children}
						</SurfaceNavBar>
					)}
					{!imageView ||
						(!imageView?.hidden && (
							<SurfaceImageViewer {...imageView} />
						))}
					<motion.section
						className={`surface-content ${
							!actionBar ? "surface-content-reduced-padding" : ""
						} ${showFullCard ? "surface-content-full-card" : ""}`}
						style={{
							"--surface-content-offset-top-scale":
								offsetTopScaleComputed,
							top: `${innerHeight / offsetTopScaleComputed}px`,
							minHeight: `${
								innerHeight -
								innerHeight / offsetTopScaleComputed
							}px`,
							...(props?.style || {}),
						}}
						variants={anim.pdpItem}
						{...props}
					>
						{showSurfaceTab && (surfaceTabContent || email) && (
							<section className={`surface-content-tab`}>
								{surfaceTabContent || email}
							</section>
						)}
						{children}
					</motion.section>
					{actionBar && (
						<SurfaceActionBar
							key="surface-action-bar"
							{...actionBar?.props}
						>
							{actionBar.children}
						</SurfaceActionBar>
					)}
				</section>
			);
		}
	)
);

export default Surface;
